import { useEffect, useMemo, useState } from 'react';
import { getDeletesForSegment } from '../playerConfigUtil';
import { getAllCompositeSegments } from '../clipUpdateDataUtilsWithSplits/compositeUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { convertSecondsToTimeString } from '@/Pages/Clip/SideBar/ClipDuration/utils';
import useDeletesWithDragChanges from '@/components/molecules/Transcript/useDeletesWithDragChanges';
import { CompositeSegment } from '@/App/remotion/MainPlayer/types/Segment/CompositeSegment';
import { ClipDeletes } from '@/domains/asset';

export default function TotalCompositeDuration({ compositeSegment }: { compositeSegment: CompositeSegment }) {
  const { clipData, fps } = useClipsContext();
  const [timeChangeDueToDrag, setTimeChangeDueToDrag] = useState(0);

  const duration = compositeSegment
    ? (compositeSegment.currentBounds.toFrame - compositeSegment.currentBounds.fromFrame) / fps
    : 0;

  const allDeletes = useMemo(() => {
    return getAllCompositeSegments(clipData).reduce(
      (acc, curr) => {
        return { ...acc, [curr.id]: getDeletesForSegment(clipData, curr.id) };
      },
      {} as Record<string, ClipDeletes>
    );
  }, [clipData]);
  const { newDeletes: finalDeletes, compositeSegmentId } = useDeletesWithDragChanges({ allDeletes });

  const compositeSegmentDeletes = useMemo(() => {
    return allDeletes[compositeSegment.id];
  }, [allDeletes, compositeSegment.id]);

  const compositeSegmentFinalDeletes = useMemo(() => {
    return finalDeletes[compositeSegment.id];
  }, [finalDeletes, compositeSegment.id]);

  const previousTotalDeleteTime = useMemo(
    () =>
      Object.values(compositeSegmentDeletes).reduce((acc, curr) => {
        return acc + (curr.bounds[1] - curr.bounds[0]);
      }, 0),
    [compositeSegmentDeletes]
  );

  useEffect(() => {
    if (compositeSegmentId !== compositeSegment.id) {
      setTimeChangeDueToDrag(0);
      return;
    }
    const currentTotalDeleteTime = Object.values(compositeSegmentFinalDeletes).reduce((acc, curr) => {
      return acc + (curr.bounds[1] - curr.bounds[0]);
    }, 0);

    setTimeChangeDueToDrag(previousTotalDeleteTime - currentTotalDeleteTime);
  }, [compositeSegment.id, compositeSegmentFinalDeletes, compositeSegmentId, finalDeletes, previousTotalDeleteTime]);

  return convertSecondsToTimeString(duration + timeChangeDueToDrag || 0, 'HH:mm:ss');
}
